import * as React from 'react';
import { PhoneInput as LibPhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import clsx from 'clsx';
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';

interface PhoneInputProps<T extends FieldValues> {
  label: string;
  field: ControllerRenderProps<T, Path<T>>;
  error?: string | undefined;
  disabled?: boolean;
  className?: string;
}

/**
 * PhoneInput Component
 *
 * A shared phone number input component wrapping 'react-international-phone'.
 * Integrates with react-hook-form via strictly typed Controller props.
 */
export const PhoneInput = <T extends FieldValues>({
  label,
  field,
  error,
  disabled,
  className,
}: PhoneInputProps<T>): React.ReactElement => (
  <div className={clsx('flex w-full flex-col gap-2', className)}>
    <label className='font-sans text-14 font-bold text-gray-520'>{label}</label>

    <div className='relative'>
      <LibPhoneInput
        defaultCountry='us'
        value={field.value}
        onChange={(phone) => field.onChange(phone)}
        disabled={disabled}
        // We override styles via classes to match your design system strictly
        inputClassName={clsx(
          '!h-[47px] !w-full !rounded-r-10 !rounded-l-none',
          '!border-l-0 !border-gray-200 !bg-white',
          '!font-jakarta !text-14 !font-semibold !text-brand-800',
          'focus:!border-brand-300 focus:!ring-0',
          disabled && '!bg-gray-50 !text-gray-500'
        )}
        countrySelectorStyleProps={{
          buttonClassName: clsx(
            '!h-[47px] !rounded-l-10 !rounded-r-none',
            '!border-gray-200 !bg-gray-50',
            '!px-3',
            disabled && '!opacity-50'
          ),
          dropdownStyleProps: {
            className:
              '!rounded-[10px] !shadow-lg !border-gray-200 !text-[14px] !font-sans !text-brand-800 !bg-white',
          },
        }}
        className='!font-sans'
      />
    </div>

    {error && <span className='text-xs font-medium text-red-500'>{error}</span>}
  </div>
);
